import { useRef, useState } from 'react';
import taxService from '@/services/billing/taxService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { TaxesModal } from '@/pages/billing';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { FiltersToFormData, getAccessToken } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import { goTo403 } from "@/utils/Navigation";
import { ColumnToggleDropdown } from '@/components/TableFilter/ColumnToggleDropdown';
import { Container } from '@/components/container';

DataTable.use(DT);
const Taxes = () => {
  const { logout } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false)
  const [contactTypeData, setContactTypeData] = useState([])
  const [itemId, setItemId] = useState(null)
  const { ability } = useAbility();
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef({});
  const formikRef = useRef();
  const canDeleteTax = ability.can('delete', 'tax');
  const canEditTax = ability.can('handle', 'tax');
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const columns = [
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    { data: 'name', title: 'Name', name: 'name' },
    {
      data: 'rate',
      title: 'Rate',
      name: 'rate',
      render: function (data, type, row) {
        return `<div>${row.rate ?? '-'}</div>`;
      }
    },
    { data: 'created_at', title: 'Created At', name: 'created_at' },

    // ---- Extended columns for missing fields ----
    // {
    //   data: 'id',
    //   title: 'ID',
    //   name: 'id',
    //   visible: false, // keep hidden but available if needed
    //   render: (data) => data ?? '-'
    // },
    {
      data: 'description',
      title: 'Description',
      name: 'description',
      render: (data) => (data ? `<div>${data}</div>` : '-')
    },
    {
      data: 'updated_at',
      title: 'Updated At',
      name: 'updated_at',
      render: (data) => data ?? '-'
    },
    // {
    //   data: 'deleted_at',
    //   title: 'Deleted At',
    //   name: 'deleted_at',
    //   render: (data) => data ?? '-'
    // },
    {
      title: 'Actions',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      },
      name: 'actions',
    },
  ];


  const defaultVisibleColumns = [
    // 'serial',
    'name',
    'rate',
    'created_at',
    'actions',
  ];

  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? taxService.updateTax : taxService.createTax;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { data, status } = await action(...args);
      if (status) {
        handleModalClose()
        refresh();
        toast.success(`Tax ${isEdit ? 'Updated' : 'Created'} Successfully`, {
          description: `${data.name} ${isEdit ? 'updated' : 'created'} successfully.`,

        })
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const validationErrors = error.errors
        const formattedErrors = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
        );

        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',

        });
      }
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status, data } = await taxService.deleteTax(actionId);
      if (status) {
        refresh()
        toast.success('Deleted Tax.', {
          description: `${data.name} Tax deleted Successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message.includes('cannot be deleted.')) {
        toast.error('Unable to delete Tax.', {
          description: `Can't delete: This Tax is in use and cannot be deleted.`,
        });
        return
      }
      toast.error('Failed to delete Tax.', {
        description: 'An error occurred while deleting tax. Please try again later.',
        action: {
          label: 'Ok',
        },
      });
    }
  }

  const handleEdit = async (id) => {
    setItemId(id)
    setOpen(true)
    setIsLoading(true)
    setMode('edit')
    try {
      const { data, status } = await taxService.getSingleTax(id);
      if (status) {
        setSelectedData(data)
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch Tax.', {
        description: 'An error occurred while fetching tax. Please try again later.',
        action: {
          label: 'Ok',
        },
      });
    } finally {
      setIsLoading(false)
    }
  }

  const handleModalClose = () => {
    setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const renderCustomActions = (row) => {
    const { id } = row;

    return (
      <DropdownMenu>
        {(canDeleteTax || canEditTax) && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-40">
          <Can I="handle" a="tax" ability={ability}>
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          </Can>
          <Can I="delete" a="tax" ability={ability}>
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const ajaxConfig = createDataTableConfig('/setup/taxes/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[2, 'desc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }
      });
    },
  });

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  return (
    <>
      <Container>
        <div className="card min-w-full mb-4">
          <div className="card-header">
            <div className='flex items-center gap-1'>
              <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
              {/* <h3 className="card-title">Taxes</h3> */}
            </div>

            <div className='flex items-center flex-wrap gap-2'>
              <Can I="handle" a="tax" ability={ability}>
                <button className='text-xs btn btn-sm btn-light' onClick={() => { setOpen(true) }}>
                  <KeenIcon icon="plus" className="w-fit btn-icon" />
                  Add New
                </button>
              </Can>
            </div>
          </div>


          <DataTable
            id="taxes-table"
            ajax={ajaxConfig}
            columns={columns}
            options={options}
            ref={tableRef}
          />

          <TaxesModal
            open={open}
            onCloseModal={handleModalClose}
            handleMode={mode}
            data={selectedData}
            onSubmit={handleSubmit}
            createFieldLoading={isLoading}
            updateFieldLoading={loading}
            ref={formikRef}
          />

          <ConfirmationModal
            show={isOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            message="Are you sure you want to delete this tax? This action is permanent and cannot be undone."
            title="Are You Sure?"
          />
        </div>
      </Container>
    </>
  )
}

export { Taxes }
